import { daysOfWeek } from 'shared/constants/daysOfWeek';
import { WidgetRequestType as WidgetRequestType } from 'shared/types/graphql';

enum FormQuestionType {
  Checkbox = 'checkbox',
  MultipleSelection = 'multipleSelection',
  TextInput = 'textInput',
  MultiLineTextInput = 'multiLineTextInput',
  RatingInput = 'ratingInput',
  DropDown = 'dropDown',
  DateInput = 'dateInput',
  TimeInput = 'timeInput',
  TextBlock = 'textBlock',
}

interface IWidgetRequestFormQuestion {
  index: number;
  type: FormQuestionType;
  prompt: string;
  defaultValue?: boolean | null;
  options?: string[];
  minValue?: number;
  maxValue?: number;
  isRequired: boolean;
  noAnswer?: boolean;
}

interface IWidgetRequestFormTemplate {
  formName?: string;
  introMessage?: string;
  questions: IWidgetRequestFormQuestion[];
}

export const getDefaultRequestFormTemplate = (requestType: WidgetRequestType): IWidgetRequestFormTemplate => {
  const formTemplate: IWidgetRequestFormTemplate = {
    formName: ``,
    introMessage: '',
    questions: [],
  };

  switch (requestType) {
    case WidgetRequestType.RequestAppointment:
      formTemplate.questions = [
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What is your reason for requesting an appointment?',
          index: 0,
        },
        {
          type: FormQuestionType.MultipleSelection,
          isRequired: true,
          prompt: 'Preferred days:',
          options: [...daysOfWeek],
          index: 1,
        },
        {
          type: FormQuestionType.DropDown,
          isRequired: true,
          prompt: 'Preferred time of day:',
          options: ['Morning', 'Afternoon', 'Either'],
          index: 2,
        },
      ];
      break;
    case WidgetRequestType.RequestBoarding:
      formTemplate.questions = [
        {
          type: FormQuestionType.DateInput,
          isRequired: true,
          prompt: 'What is the start date you need boarding for?',
          index: 0,
        },
        {
          type: FormQuestionType.DateInput,
          isRequired: true,
          prompt: 'What is the end date you need boarding for?',
          index: 1,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What pet(s) will be staying with us?',
          index: 2,
        },
        {
          type: FormQuestionType.MultiLineTextInput,
          isRequired: false,
          prompt: 'Please include any special requests or any other additional information we need here:',
          index: 3,
        },
      ];
      break;
    case WidgetRequestType.RequestGroomingAppoinment:
      formTemplate.questions = [
        {
          type: FormQuestionType.MultipleSelection,
          isRequired: true,
          prompt: 'Please select all services you would like:',
          options: ['Nail Trim', 'Gland Expression', 'Ear Cleaning', 'Cut', 'Bath and Brush'],
          index: 0,
        },
        {
          type: FormQuestionType.MultipleSelection,
          isRequired: false,
          prompt: 'Preferred days:',
          options: [...daysOfWeek],
          index: 1,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'Which pets need grooming?',
          index: 2,
        },
      ];
      break;
    case WidgetRequestType.RequestMedicalRecords:
      formTemplate.questions = [
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What date do you need the records from?',
          index: 0,
        },
        {
          type: FormQuestionType.MultipleSelection,
          isRequired: true,
          prompt: 'What type of records do you need?',
          options: ['Vaccine History', 'Medical History', 'Surgery History', 'Allergies', 'Full Records'],
          index: 1,
        },
        {
          type: FormQuestionType.DateInput,
          isRequired: false,
          prompt: 'When do you need these records?',
          index: 2,
        },
      ];
      break;
    case WidgetRequestType.RequestRxRefill:
      formTemplate.questions = [
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What is the name of the medication (or food) that you need?',
          index: 0,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: false,
          prompt: 'What is the dosage amount you need?',
          index: 1,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: false,
          prompt: 'What is the length of the supply you need?',
          index: 2,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: false,
          prompt: 'How long has your pet been taking this medication (or food)?',
          index: 3,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What other preventative medications is your pet taking (flea, tick, heartworm prevention, etc.)?',
          index: 4,
        },
        {
          type: FormQuestionType.TextInput,
          isRequired: false,
          prompt: 'What other medications is your pet taking (other than the above)?',
          index: 5,
        },
        {
          type: FormQuestionType.DateInput,
          isRequired: false,
          prompt: 'What date do you need this medication (or food) by?',
          index: 6,
        },
      ];
      break;
    case WidgetRequestType.RequestVirtualConsult:
      formTemplate.questions = [
        {
          type: FormQuestionType.MultiLineTextInput,
          isRequired: true,
          prompt: 'What is your reason for requesting a virtual consultation?',
          index: 0,
        },
        {
          type: FormQuestionType.TextBlock,
          isRequired: false,
          prompt: 'If this is an emergency, please call us or your closest emergency veterinary clinic right away.',
          index: 1,
        },
      ];
      break;
    case WidgetRequestType.TalkToStaff:
      formTemplate.questions = [
        {
          type: FormQuestionType.MultiLineTextInput,
          isRequired: true,
          prompt: 'Please include any information we may need to handle your request:',
          index: 0,
        },
      ];
      break;
    case WidgetRequestType.InboundSms:
      formTemplate.questions = [
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'What is your reason for requesting an appointment?',
          index: 0,
        },
        {
          type: FormQuestionType.MultipleSelection,
          isRequired: true,
          prompt: 'Preferred days:',
          options: [...daysOfWeek],
          index: 1,
        },
        {
          type: FormQuestionType.DropDown,
          isRequired: true,
          prompt: 'Preferred time of day:',
          options: ['Morning', 'Afternoon', 'Either'],
          index: 2,
        },
      ];
      break;
    default:
      formTemplate.questions = [
        {
          type: FormQuestionType.TextInput,
          isRequired: true,
          prompt: 'Please include any information we may need to handle your request:',
          index: 0,
        },
      ];
      break;
  }

  return formTemplate;
};
